import { useState } from 'react';

import type { SchemaChatResolver_GetChats } from 'cpool-tables/backend-resolvers';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

import { ChatListItemMenu } from './ChatListItemMenu';
import { CHAT_LIST_VARIANTS, type ChatListVariant } from '~/features/whatsapp/constants/chatListVariants';
import { deriveChatDisplayName } from '~/features/whatsapp/utils/deriveChatDisplayName';
import {
    CHAT_STATUS_PILL_CLASSES,
    CHAT_UNLINKED_PILL_CLASS,
    deriveChatStatusLabel,
} from '~/features/whatsapp/utils/deriveChatStatus';
import { getInitials } from '~/features/whatsapp/utils/getInitials';
import { isPartnerChat } from '~/features/whatsapp/utils/isPartnerChat';
import { dateTimeTwoDigitFormat } from '~/lib/i18n';
import { cn } from '~/utils/cnTailwind';

type Props = {
    chat: SchemaChatResolver_GetChats[number];
    unreadCount: number;
    onClick: () => void;
    isActive?: boolean;
    variant?: ChatListVariant;
};

export const ChatListItem = ({
    chat,
    isActive = false,
    onClick,
    unreadCount,
    variant = CHAT_LIST_VARIANTS.PANEL,
}: Props) => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const displayName = deriveChatDisplayName(chat);
    const statusLabel = deriveChatStatusLabel(chat);
    const isUnlinked = chat.loanRequestIds.length === 0 && !isPartnerChat(chat);
    const isPage = variant === CHAT_LIST_VARIANTS.PAGE;
    const lastMessageText = chat.lastMessageReactionEmoji
        ? t('WHATSAPP_CHAT.LIST.REACTION_PREVIEW', {
              emoji: chat.lastMessageReactionEmoji,
              message: chat.lastMessagePreview ?? '',
              name: displayName,
          })
        : chat.lastMessagePreview ?? '';

    return (
        <div className={cn('group relative', isPage && 'mx-4')}>
            <button
                type="button"
                onClick={onClick}
                aria-current={(isPage && isActive) || undefined}
                className={cn(
                    'flex w-full items-start text-left',
                    isPage
                        ? 'gap-2 rounded-md border-l-2 p-3 shadow-sm'
                        : 'gap-3 border-b border-outline-light px-4 py-3 hover:bg-surface-light',
                    isPage && isActive && 'border-blue-text bg-grey-stroke',
                    isPage && !isActive && 'border-transparent bg-white hover:bg-grey-stroke',
                )}
                data-testid="chat-list-item"
            >
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center rounded-full',
                        isPage
                            ? 'size-12 bg-grey-hover text-sm font-bold text-midnight'
                            : 'size-10 bg-grey-light text-sm font-semibold text-grey',
                    )}
                >
                    {getInitials(displayName)}
                </span>
                <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-midnight">{displayName}</span>
                        {chat.lastMessageAt != null && (
                            <span className="shrink-0 text-sm text-grey">
                                {dateTimeTwoDigitFormat(new Date(chat.lastMessageAt))}
                            </span>
                        )}
                    </span>
                    <span className="mt-0.5 flex items-center justify-between gap-2">
                        <span className="truncate text-sm text-grey">{lastMessageText}</span>
                        {unreadCount > 0 && (
                            <span
                                className={cn(
                                    'flex size-5 shrink-0 items-center justify-center rounded-full bg-midnight text-xs font-semibold text-white',
                                    // Menu trigger takes this spot while shown.
                                    'group-focus-within:invisible group-hover:invisible',
                                    menuOpen && 'invisible',
                                )}
                            >
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </span>
                    <span
                        className={cn(
                            'mt-1 inline-block rounded px-1.5 py-0.5 text-xs font-semibold',
                            CHAT_STATUS_PILL_CLASSES[statusLabel],
                        )}
                    >
                        {t(`WHATSAPP_CHAT.STATUS.${statusLabel.toUpperCase()}` as ParseKeys)}
                    </span>
                    {isUnlinked && (
                        <span
                            className={cn(
                                'ml-1 mt-1 inline-block rounded px-1.5 py-0.5 text-xs font-semibold',
                                CHAT_UNLINKED_PILL_CLASS,
                            )}
                        >
                            {t('WHATSAPP_CHAT.LIST.UNLINKED_PILL')}
                        </span>
                    )}
                </span>
            </button>
            <ChatListItemMenu
                chatId={chat.id}
                resolved={chat.resolved}
                open={menuOpen}
                onOpenChange={setMenuOpen}
                className={cn(
                    'absolute top-8',
                    isPage ? 'right-3' : 'right-4',
                    !menuOpen &&
                        'opacity-0 focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100',
                )}
            />
        </div>
    );
};
